import type {
    Revision,
    RevisionFileBlobMetadata,
    RevisionMetadataUpdate,
} from '@gitbook/hive-core';

import type { GetRevisionOptions } from './HiveClient';

/**
 * HiveClient uses an ObjectStorage interface to do any operations on object storage.
 * This allows object storage to be mocked for testing or to be implemented using different backends.
 */
export interface ObjectStorageFetchers {
    /**
     * Fetch a document buffer from object storage.
     */
    getDocumentBuffer: (
        contentId: string,
        documentId: string,
        options: { noCache?: boolean }
    ) => Promise<{ buffer: Uint8Array; normalized: boolean }>;

    /**
     * Returns a Revision from object storage.
     * If options.mask is provided, only the specified fields will be returned.
     */
    getRevision: <MaskKeys extends (keyof Revision)[] | undefined>(
        contentId: string,
        revisionId: string,
        options: GetRevisionOptions<MaskKeys>
    ) => Promise<{ revision: Revision; normalized: boolean }>;

    /**
     * Creates a new document. Optionally, a document ID can be provided.
     */
    createDocument: (
        contentId: string,
        document: Uint8Array,
        options: { documentId?: string }
    ) => Promise<{ documentId: string }>;

    /**
     * Creates a new revision. If options.force is true, it will overwrite any existing revision with the same ID.
     */
    createRevision: (
        contentId: string,
        revision: Revision,
        options: { force?: boolean }
    ) => Promise<{ revisionId: string }>;

    /**
     * Updates the metadata of an existing revision.
     */
    updateRevisionMetadata: (
        contentId: string,
        revisionId: string,
        metadata: RevisionMetadataUpdate
    ) => Promise<Revision>;

    /**
     * Creates a new file blob in object storage.
     */
    createFileBlob: (
        contentId: string,
        metadata: RevisionFileBlobMetadata,
        blob: Blob | ReadableStream
    ) => Promise<{ blobId: string }>;
}

/**
 * Creates an ObjectStorageFetchers implementation that uses fetch to make requests to a REST API.
 *
 * You must provide a function to get the base URL for a given content ID, and a function to perform
 * the request (see HiveClient constructor).
 */
export function createObjectStorageFetch(
    getBaseURL: (contentId: string) => Promise<string>,
    request
): ObjectStorageFetchers {
    return {
        getDocumentBuffer: async (contentId, documentId, options) => {
            const baseURL = await getBaseURL(contentId);
            const response = await request(
                {
                    baseURL,
                    contentId,
                    path: '/documents/:documentId',
                    params: {
                        path: { documentId },
                        query: {
                            ...(options.noCache ? { noCache: 'true' } : {}),
                        },
                    },
                },
                {
                    method: 'GET',
                }
            );
            const arrayBuffer = await response.arrayBuffer();
            return {
                buffer: new Uint8Array(arrayBuffer),
                normalized: response.headers.get('X-Hive-Normalized') === 'true',
            };
        },

        getRevision: async (contentId, revisionId, options) => {
            const { mask, metadata = true } = options;
            const baseURL = await getBaseURL(contentId);
            const response = await request(
                {
                    contentId,
                    baseURL,
                    path: '/revisions/:revisionId',
                    params: {
                        path: { revisionId },
                        query: {
                            ...(mask ? { mask: mask.join(',') } : {}),
                            ...(metadata === false ? { metadata: 'false' } : {}),
                        },
                    },
                },
                undefined
            );
            const revision = await response.json();
            return { revision, normalized: response.headers.get('X-Hive-Normalized') === 'true' };
        },

        createDocument: async (contentId, body, options) => {
            const baseURL = await getBaseURL(contentId);
            const response = await request(
                {
                    contentId,
                    baseURL,
                    path: '/documents',
                    params: {
                        query: options.documentId ? { documentId: options.documentId } : {},
                    },
                },
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/octet-stream',
                    },
                    body,
                }
            );
            const result = (await response.json()) as { documentId: string };
            return result;
        },

        createRevision: async (contentId, revision, options) => {
            const { force = false } = options;
            const baseURL = await getBaseURL(contentId);
            const response = await request(
                {
                    contentId,
                    baseURL,
                    path: '/revisions',
                    params: {
                        query: {
                            ...(force ? { force: 'true' } : {}),
                        },
                    },
                },
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(revision),
                }
            );
            const result = (await response.json()) as { revisionId: string };
            return result;
        },

        updateRevisionMetadata: async (contentId, revisionId, metadata) => {
            const baseURL = await getBaseURL(contentId);
            const response = await request(
                {
                    contentId,
                    baseURL,
                    path: '/revisions/:revisionId',
                    params: {
                        path: { revisionId },
                    },
                },
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(metadata),
                }
            );
            const result = (await response.json()) as Revision;
            return result;
        },

        createFileBlob: async (contentId, metadata, blob) => {
            const baseURL = await getBaseURL(contentId);
            const response = await request(
                {
                    contentId,
                    baseURL,
                    path: '/blobs',
                    params: {
                        query: {
                            name: metadata.name,
                        },
                    },
                },
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': metadata.contentType,
                    },
                    body: blob,
                    ...(blob instanceof ReadableStream ? { duplex: 'half' } : {}),
                }
            );
            const { blobId } = (await response.json()) as { blobId: string };
            return { blobId };
        },
    };
}
